"use client";

import { motion, useSpring } from "motion/react";
import { memo, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useViewerLanguage } from "@/lib/i18n";
import { useChartAxisSlots } from "../chart-axis-frame";
import {
  resolveTooltipBoxMotion,
  type SpringConfig,
  useChartConfig,
} from "../chart-config-context";
import {
  chartCssVars,
  type LineConfig,
  useChart,
  useChartStable,
} from "../chart-context";
import { useChartLegendHover } from "../chart-legend-hover";
import { weekdayDateFmt } from "../chart-formatters";
import { formatXLabel } from "../x-scale";
import { useChartProjection } from "../projection";
import type { IndicatorFadeEdges } from "../indicator-fade";
import { DateTicker } from "./date-ticker";
import { TooltipBox } from "./tooltip-box";
import { TooltipContent, type TooltipRow } from "./tooltip-content";
import { TooltipDot } from "./tooltip-dot";
import { TooltipIndicator } from "./tooltip-indicator";

export interface ChartTooltipProps {
  /** Whether to show the date pill at bottom. Default: true */
  showDatePill?: boolean;
  /** Whether to show the vertical crosshair line. Default: true */
  showCrosshair?: boolean;
  /** Whether to show dots on the lines. Default: true */
  showDots?: boolean;
  /** Dot style: filled circle or transparent ring. Default: "dot" */
  dotVariant?: "dot" | "ring";
  /** Dot / ring radius in pixels. Default: 5 */
  dotSize?: number;
  /** Ring corner radius as a fraction of side length (0 = square, 0.5 = circle). */
  dotRadiusFraction?: number;
  /** Multiplier applied to the computed dot / ring pixel radius. Default: 1 */
  dotScale?: number;
  /** Ring stroke width in pixels. Default: 1.5 for ring variant */
  dotStrokeWidth?: number;
  /**
   * Color for the crosshair/indicator line. When a function, receives the hovered point
   * (e.g. for candlestick: match candle color from close vs open). Default: --chart-crosshair.
   */
  indicatorColor?: string | ((point: Record<string, unknown>) => string);
  /** Custom content renderer for the tooltip box */
  content?: (props: {
    point: Record<string, unknown>;
    index: number;
  }) => React.ReactNode;
  /** Custom row renderer - return array of TooltipRow */
  rows?: (point: Record<string, unknown>) => TooltipRow[];
  /**
   * Override tooltip dot fill. When omitted and `rows` is set, dot colors match row colors.
   * When a function, receives the hovered point and line config.
   */
  dotColor?:
    | string
    | ((point: Record<string, unknown>, line: LineConfig) => string);
  /** Additional content to show below rows (e.g., markers) */
  children?: React.ReactNode;
  /** Custom class name */
  className?: string;
  /** Per-chart override for the crosshair / dot / date-pill spring. */
  springConfig?: SpringConfig;
  /**
   * When `true`, the floating panel uses the crosshair spring and stays in sync.
   * Default `false` — panel follow uses `damping` (`20`).
   */
  matchCrosshair?: boolean;
  /**
   * Spring damping for the floating tooltip panel when `matchCrosshair` is `false`.
   * `0` disables spring motion (instant). Default: `20`.
   */
  damping?: number;
  /** SVG stroke dash pattern for the crosshair. Omit for solid. */
  indicatorDasharray?: string;
  /** Vertical crosshair fade: `both`, `top`, `bottom`, or `none` (solid). Default: `both`. */
  indicatorFadeEdges?: IndicatorFadeEdges;
  /** Crosshair fade zone size (% of height). Default: `10`. */
  indicatorFadeLength?: number;
  /** Per-chart override for the floating-panel spring. */
  boxSpringConfig?: SpringConfig;
  /** Inline styles for the tooltip panel (background, blur, etc.). */
  panelStyle?: React.CSSProperties;
  /**
   * Tooltip panel background color (CSS variable or color value).
   * Default: `var(--chart-tooltip-background)`.
   */
  backgroundColor?: string;
  anchor?: "plot" | "element";
}

interface ChartTooltipInnerProps extends ChartTooltipProps {
  container: HTMLElement;
}

const ChartTooltipInner = memo(function ChartTooltipInner({
  showDatePill = true,
  showCrosshair = true,
  showDots = true,
  dotVariant = "dot",
  dotSize = 5,
  dotRadiusFraction,
  dotScale = 1,
  dotStrokeWidth,
  indicatorColor: indicatorColorProp,
  content,
  rows: rowsRenderer,
  dotColor: dotColorProp,
  children,
  className = "",
  container,
  springConfig,
  matchCrosshair = false,
  damping,
  indicatorDasharray,
  indicatorFadeEdges,
  indicatorFadeLength,
  boxSpringConfig,
  panelStyle,
  backgroundColor,
  anchor = "plot",
}: ChartTooltipInnerProps) {
  const {
    tooltipData,
    width,
    height,
    innerHeight,
    margin,
    columnWidth,
    lines,
    xAccessor,
    xScaleKind,
    dateLabels,
    containerRef,
    orientation,
    barXAccessor,
    bandWidth,
    squareSnap,
  } = useChart();
  const { tooltipSpring, tooltipBoxSpring } = useChartConfig();
  const { filterIndex: legendFilterIndex } = useChartLegendHover();
  const axisSlots = useChartAxisSlots();
  const isometric =
    (useChartProjection()?.isometric ?? false) || anchor === "element";

  const isHorizontal = orientation === "horizontal";
  const discreteInteraction = dateLabels.length > 60;

  const resolvedDotSize = useMemo(() => {
    if (dotVariant !== "ring" || !bandWidth || lines.length === 0) {
      return dotSize * dotScale;
    }
    const seriesCount = lines.length;
    const gap = squareSnap?.groupGap ?? (seriesCount > 1 ? 4 : 0);
    const squareSize = (bandWidth - gap * (seriesCount - 1)) / seriesCount;
    return (squareSize / 2) * dotScale;
  }, [
    bandWidth,
    dotScale,
    dotSize,
    dotVariant,
    lines.length,
    squareSnap?.groupGap,
  ]);
  const boxMotion = useMemo(() => {
    if (isometric) {
      return {
        animate: false,
        springConfig: boxSpringConfig ?? tooltipBoxSpring,
      };
    }
    if (boxSpringConfig) {
      return {
        animate: !discreteInteraction,
        springConfig: boxSpringConfig,
      };
    }
    if (matchCrosshair) {
      return {
        animate: !discreteInteraction,
        springConfig: springConfig ?? tooltipSpring,
      };
    }
    return resolveTooltipBoxMotion(damping);
  }, [
    boxSpringConfig,
    damping,
    discreteInteraction,
    isometric,
    matchCrosshair,
    springConfig,
    tooltipBoxSpring,
    tooltipSpring,
  ]);

  const visible = tooltipData !== null;
  const x = tooltipData?.x ?? 0;
  const xWithMargin = x + margin.left;

  // For horizontal charts, get the y position from the first line's yPosition (center of bar)
  const firstLineDataKey = lines[0]?.dataKey;
  const firstLineY = firstLineDataKey
    ? (tooltipData?.yPositions[firstLineDataKey] ?? 0)
    : 0;
  const yWithMargin = firstLineY + margin.top;
  const elementY = tooltipData
    ? (Object.values(tooltipData.yPositions)[0] ?? 0) + margin.top
    : margin.top;

  const seriesRows = useMemo(() => {
    if (!tooltipData) {
      return [];
    }

    if (rowsRenderer) {
      return rowsRenderer(tooltipData.point);
    }

    // Default: generate rows from registered lines
    return lines.map((line) => ({
      color: line.stroke,
      label: line.dataKey,
      value: (tooltipData.point[line.dataKey] as number) ?? 0,
    }));
  }, [tooltipData, lines, rowsRenderer]);

  // A selection made at the legend filters the tooltip down to that one series;
  // one made on chart geometry never does. Rows are only correlated with the
  // legend positionally, so the filter is skipped unless the renderer emitted
  // exactly one row per registered series — a chart like candlestick emits four
  // rows for one series and two legend entries, where the index would mean
  // nothing.
  const seriesFilterIndex = useMemo(() => {
    if (legendFilterIndex === null || lines.length === 0) {
      return null;
    }
    if (seriesRows.length !== lines.length) {
      return null;
    }
    if (legendFilterIndex < 0 || legendFilterIndex >= seriesRows.length) {
      return null;
    }
    return legendFilterIndex;
  }, [legendFilterIndex, lines.length, seriesRows.length]);

  const tooltipRows = useMemo(() => {
    if (seriesFilterIndex === null) {
      return seriesRows;
    }
    const row = seriesRows[seriesFilterIndex];
    return row ? [row] : seriesRows;
  }, [seriesRows, seriesFilterIndex]);

  const resolveDotColor = useMemo(() => {
    return (line: LineConfig, index: number): string => {
      if (rowsRenderer && seriesRows[index]?.color) {
        return seriesRows[index].color;
      }
      if (dotColorProp != null) {
        if (typeof dotColorProp === "function" && tooltipData) {
          return dotColorProp(tooltipData.point, line);
        }
        if (typeof dotColorProp === "string") {
          return dotColorProp;
        }
      }
      return line.stroke;
    };
  }, [dotColorProp, rowsRenderer, tooltipData, seriesRows]);

  // Resolve indicator color (static or from hovered point)
  const indicatorColor = useMemo(() => {
    if (indicatorColorProp == null) {
      return chartCssVars.crosshair;
    }
    if (typeof indicatorColorProp === "function") {
      return tooltipData
        ? indicatorColorProp(tooltipData.point)
        : chartCssVars.crosshair;
    }
    return indicatorColorProp;
  }, [indicatorColorProp, tooltipData]);

  const { intlLocale } = useViewerLanguage();

  // Title from date, category, or raw x-value
  const title = useMemo(() => {
    if (!tooltipData) {
      return undefined;
    }
    // For bar charts (horizontal or vertical), use the category name
    if (barXAccessor) {
      return barXAccessor(tooltipData.point);
    }
    const raw = xAccessor(tooltipData.point);
    if (xScaleKind === "time" && raw instanceof Date) {
      return weekdayDateFmt(intlLocale).format(raw);
    }
    return formatXLabel(raw, xScaleKind ?? "time", intlLocale);
  }, [tooltipData, barXAccessor, xAccessor, xScaleKind, intlLocale]);

  const tooltipContent = (
    <>
      {/* Crosshair indicator - rendered as SVG overlay */}
      {showCrosshair && !isometric && (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          height="100%"
          width="100%"
        >
          <g transform={`translate(${margin.left},${margin.top})`}>
            <TooltipIndicator
              animate={!discreteInteraction}
              colorEdge={indicatorColor}
              colorMid={indicatorColor}
              columnWidth={columnWidth}
              fadeEdges={
                indicatorDasharray ? "none" : (indicatorFadeEdges ?? "both")
              }
              fadeLength={indicatorFadeLength}
              height={innerHeight}
              springConfig={springConfig}
              strokeDasharray={indicatorDasharray}
              visible={visible}
              width="line"
              x={x}
            />
          </g>
        </svg>
      )}

      {/* Dots on bars/lines - show for vertical charts only */}
      {showDots && visible && !isHorizontal && (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          height="100%"
          width="100%"
        >
          <g transform={`translate(${margin.left},${margin.top})`}>
            {lines.map((line, index) =>
              isometric &&
              tooltipData?.yPositions[line.dataKey] === undefined ? null : (
                <TooltipDot
                  color={resolveDotColor(line, index)}
                  cornerRadiusFraction={
                    dotVariant === "ring" ? dotRadiusFraction : undefined
                  }
                  key={line.dataKey}
                  size={resolvedDotSize}
                  springConfig={springConfig}
                  strokeColor={chartCssVars.background}
                  strokeWidth={
                    dotVariant === "ring" ? dotStrokeWidth : undefined
                  }
                  variant={dotVariant}
                  visible={
                    visible &&
                    (seriesFilterIndex === null || seriesFilterIndex === index)
                  }
                  x={tooltipData?.xPositions?.[line.dataKey] ?? x}
                  y={tooltipData?.yPositions[line.dataKey] ?? 0}
                />
              )
            )}
          </g>
        </svg>
      )}

      {/* Tooltip Box */}
      <TooltipBox
        animate={boxMotion.animate}
        backgroundColor={backgroundColor}
        className={className}
        containerHeight={height}
        containerRef={containerRef}
        containerWidth={width}
        panelStyle={panelStyle}
        placement={isometric ? "above" : undefined}
        springConfig={boxMotion.springConfig}
        top={isHorizontal || isometric ? undefined : margin.top}
        visible={visible}
        x={xWithMargin}
        y={isometric ? elementY : isHorizontal ? yWithMargin : margin.top}
      >
        {content && tooltipData
          ? content({
              point: tooltipData.point,
              index: tooltipData.index,
            })
          : !content && (
              <TooltipContent rows={tooltipRows} title={title}>
                {children}
              </TooltipContent>
            )}
      </TooltipBox>
    </>
  );

  // The pill belongs in the x-axis rail, whose labels it replaces while
  // hovering. Framed charts keep that rail outside the plot box, so portalling
  // it there is what puts it back in line with the labels instead of floating
  // above them. Both boxes share the plot's left edge, so `x` needs no shift.
  const pillRail = axisSlots?.xRail ?? null;

  const datePill = (
    <DatePillTracker
      currentIndex={tooltipData?.index ?? 0}
      discreteInteraction={discreteInteraction}
      enabled={showDatePill && !isHorizontal && !isometric}
      inAxisRail={pillRail !== null}
      labels={dateLabels}
      springConfig={springConfig}
      visible={visible}
      xWithMargin={xWithMargin}
    />
  );

  return (
    <>
      {createPortal(tooltipContent, container)}
      {createPortal(datePill, pillRail ?? container)}
    </>
  );
});

export function ChartTooltip(props: ChartTooltipProps) {
  const { containerRef } = useChartStable();
  const [mounted, setMounted] = useState(false);

  // Only render portals on client side after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  const container = containerRef.current;
  if (!(mounted && container)) {
    return null;
  }

  return <ChartTooltipInner {...props} container={container} />;
}

ChartTooltip.displayName = "ChartTooltip";

interface DatePillTrackerProps {
  enabled: boolean;
  visible: boolean;
  labels: string[];
  currentIndex: number;
  xWithMargin: number;
  discreteInteraction: boolean;
  springConfig?: SpringConfig;
  /** Pill sits inside the x-axis rail rather than over the plot's bottom edge. */
  inAxisRail: boolean;
}

// Inner-only-on-visible so `useSpring` initializes at the real cursor x
// instead of `margin.left` on first hover.
function DatePillTracker(props: DatePillTrackerProps) {
  if (!(props.enabled && props.visible && props.labels.length > 0)) {
    return null;
  }
  return <DatePillTrackerInner {...props} />;
}

function DatePillTrackerInner({
  labels,
  currentIndex,
  xWithMargin,
  discreteInteraction,
  springConfig,
  visible,
  inAxisRail,
}: DatePillTrackerProps) {
  const { tooltipSpring } = useChartConfig();
  const effectiveSpring = springConfig ?? tooltipSpring;
  const animatedX = useSpring(xWithMargin, effectiveSpring);

  if (!discreteInteraction) {
    animatedX.set(xWithMargin);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to jump the animatedX when the visible prop changes
  useEffect(() => {
    animatedX.set(xWithMargin);
  }, [animatedX, visible]);

  return (
    <motion.div
      className="pointer-events-none absolute z-50"
      style={{
        left: discreteInteraction ? xWithMargin : animatedX,
        transform: "translateX(-50%)",
        ...(inAxisRail ? { top: 0 } : { bottom: 4 }),
      }}
    >
      <DateTicker
        currentIndex={currentIndex}
        labels={labels}
        visible={visible}
      />
    </motion.div>
  );
}

export default ChartTooltip;
